/**
 * <complementary-look> — the Birlikte Al card row.
 *
 * Ported from the "Stili Tamamla" section of Kadriye Baştürk's Hydrogen
 * storefront (app/components/CompleteLookSection.tsx + ProductForm.tsx).
 *
 * The cards never add to the cart themselves. Each is a checkbox, and the
 * page's own "Sepete Ekle" sends the main product plus everything ticked in a
 * SINGLE /cart/add request — so the shopper makes one decision and the cart
 * drawer opens once, holding everything they chose.
 *
 * Three states per card, two flags:
 *   unticked                       → nothing
 *   ticked, no variant  TENTATIVE  → multi-variant card, chips revealed, the
 *                                    main button locks with "<Option> Seçiniz"
 *   ticked, variant     COMMITTED  → contributes a line to the batch, and only
 *                                    now grows an adet stepper
 * Single-variant products skip the tentative phase; one tap commits.
 *
 * A committed card carries a quantity of its own (1..99). It reaches /cart/add
 * as that line's `quantity`, and the main button counts UNITS, not cards, so
 * "(4 ürün)" means four things arriving rather than four products chosen.
 *
 * The interactive roles are attached HERE, not in the Liquid: a card that
 * announces itself as a button and shows a pointer cursor before this script
 * runs is a surface that lies. Liquid ships inert markup; this file makes it
 * a control.
 */
if (!customElements.get('complementary-look')) {
  /** Shopify's own cart quantity field stops at nothing, but a card in a
   *  suggestion row is not where anyone means to order a hundred of something,
   *  and an unbounded field is one fat-fingered keypress from a 111-unit line. */
  const LOOK_MAX_QUANTITY = 99;

  customElements.define(
    'complementary-look',
    class ComplementaryLook extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;

        this.cards = Array.from(this.querySelectorAll('[data-look-card]'));
        if (!this.cards.length) return;

        this.scroller = this.querySelector('.complementary-look__scroller');
        this.status = this.querySelector('[data-look-status]');
        this.selections = new Map();
        this.submitting = false;

        this.upgradeCards();
        this.restoreSelections();

        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onQtyInput = this.onQtyInput.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        // Both: `input` follows the typing, `change` is where the field is
        // corrected — blur, Enter, and the native arrow keys all land there.
        this.addEventListener('input', this.onQtyInput);
        this.addEventListener('change', this.onQtyInput);
        this.scroller?.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        // Capture phase on the document, deliberately: ProductForm registers its
        // own submit listener on the form in its constructor, and listeners on
        // the target fire in registration order regardless of phase. Only an
        // ancestor's capture listener is guaranteed to run first.
        document.addEventListener('submit', this.onSubmitCapture, true);

        // Only now does the row claim to be interactive.
        this.dataset.bound = 'true';

        this.cards.forEach((card) => this.renderCard(card));
        this.syncMainButton();
        this.onScroll();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmitCapture, true);
        window.removeEventListener('resize', this.onScroll);
        // The block is replaced wholesale on a colour swap; leave the main
        // button as we found it rather than with a stale "(2 ürün)" label.
        this.restoreMainButton();
      }

      /** Roles, tab stops and the pressed state belong to the behaviour, so
       *  they are attached once the behaviour exists. */
      upgradeCards() {
        this.cards.forEach((card) => {
          const toggle = card.querySelector('[data-look-toggle]');
          if (!toggle) return;
          toggle.setAttribute('role', 'button');
          toggle.setAttribute('tabindex', '0');
          toggle.setAttribute('aria-pressed', 'false');
          const expand = card.querySelector('[data-look-expand]');
          if (expand) {
            toggle.setAttribute('aria-expanded', 'false');
            if (expand.id) toggle.setAttribute('aria-controls', expand.id);
          }
        });
        this.querySelectorAll('[data-look-scroll]').forEach((a) => {
          a.hidden = false;
        });
      }

      /* ── main product form ─────────────────────────────────────────── */

      get mainForm() {
        return document.getElementById(`product-form-${this.dataset.sectionId}`);
      }

      get mainButton() {
        return document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      }

      get errorBox() {
        return this.mainForm?.querySelector('.product-form__error-message') || null;
      }

      /* ── selection state ───────────────────────────────────────────── */

      get storageKey() {
        return `birlikte-al:${this.dataset.sectionId}:${this.dataset.productId || ''}`;
      }

      selectionFor(card) {
        const id = card.dataset.productId;
        if (!this.selections.has(id)) {
          this.selections.set(id, { checked: false, variantId: '', quantity: 1 });
        }
        return this.selections.get(id);
      }

      /** Whole numbers, 1..LOOK_MAX_QUANTITY. Every path into a quantity goes
       *  through here: a number input hands back '' for anything it cannot
       *  parse, sessionStorage can hold whatever an older build wrote, and
       *  either would otherwise reach /cart/add as NaN. */
      clampQuantity(value) {
        const n = Math.floor(Number(value));
        if (!Number.isFinite(n) || n < 1) return 1;
        return Math.min(n, LOOK_MAX_QUANTITY);
      }

      /** Ticks survive a trip to a card's product page and back. Without this a
       *  shopper who taps through to read a full product name loses every tick
       *  they had made, with no warning that they would. */
      persistSelections() {
        try {
          const live = [...this.selections].filter(([, s]) => s.checked);
          if (!live.length) sessionStorage.removeItem(this.storageKey);
          else sessionStorage.setItem(this.storageKey, JSON.stringify(live));
        } catch (e) {
          /* private mode, quota, disabled storage — ticks are a convenience */
        }
      }

      restoreSelections() {
        let saved;
        try {
          saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        } catch (e) {
          return;
        }
        if (!Array.isArray(saved)) return;
        const present = new Set(this.cards.map((c) => c.dataset.productId));
        saved.forEach(([id, sel]) => {
          // A restored tick is only honoured for a card still on the page: the
          // list can change between visits (metafield edited, product sold out).
          if (present.has(id) && sel && sel.checked) {
            this.selections.set(id, {
              checked: true,
              variantId: sel.variantId || '',
              quantity: this.clampQuantity(sel.quantity),
            });
          }
        });
      }

      committedLines() {
        const lines = [];
        for (const card of this.cards) {
          const sel = this.selections.get(card.dataset.productId);
          if (sel && sel.checked && sel.variantId) {
            lines.push({ id: Number(sel.variantId), quantity: this.clampQuantity(sel.quantity) });
          }
        }
        return lines;
      }

      pendingCard() {
        return this.cards.find((card) => {
          const sel = this.selections.get(card.dataset.productId);
          return sel && sel.checked && !sel.variantId;
        });
      }

      /* ── interaction ───────────────────────────────────────────────── */

      onClick(event) {
        const arrow = event.target.closest('[data-look-scroll]');
        if (arrow) {
          if (arrow.disabled) return;
          this.scrollByPage(arrow.dataset.lookScroll === 'next' ? 1 : -1);
          return;
        }

        // The "ürüne git" link sits outside the toggle surface and navigates.
        if (event.target.closest('[data-look-link]')) return;

        const chip = event.target.closest('[data-look-variant]');
        if (chip) {
          if (chip.disabled) return;
          this.pickVariant(chip.closest('[data-look-card]'), chip.dataset.variantId);
          return;
        }

        const step = event.target.closest('[data-look-qty]');
        if (step) {
          this.stepQuantity(step.closest('[data-look-card]'), step.dataset.lookQty === 'up' ? 1 : -1);
          return;
        }
        // A tap that lands in the number field itself is an edit, not a tick.
        // The stepper sits outside the toggle surface, so nothing would fire
        // today — this is the guard that keeps that true if it ever moves in.
        if (event.target.closest('[data-look-qty-wrap]')) return;

        const toggle = event.target.closest('[data-look-toggle]');
        if (toggle) this.toggleCard(toggle.closest('[data-look-card]'));
      }

      onKeydown(event) {
        const chip = event.target.closest('[data-look-variant]');
        if (chip) {
          this.onChipKeydown(event, chip);
          return;
        }
        const field = event.target.closest('[data-look-qty-input]');
        if (field) {
          // Enter in a number field is "I am done", not "buy this". The row is
          // a sibling of the product form today, but an Enter that ever reached
          // one would add to cart from a keypress meant to close a stepper.
          // Blur settles the value through the `change` path below.
          if (event.key === 'Enter') {
            event.preventDefault();
            field.blur();
          }
          return;
        }
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        const toggle = event.target.closest('[data-look-toggle]');
        if (!toggle) return;
        event.preventDefault();
        this.toggleCard(toggle.closest('[data-look-card]'));
      }

      /** A radiogroup is navigated with the arrow keys, not with Tab — the group
       *  is one tab stop and the arrows move within it. */
      onChipKeydown(event, chip) {
        const KEYS = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          if (chip.disabled) return;
          event.preventDefault();
          this.pickVariant(chip.closest('[data-look-card]'), chip.dataset.variantId);
          return;
        }
        if (!KEYS.includes(event.key)) return;
        event.preventDefault();
        const group = Array.from(
          chip.closest('[data-look-expand]').querySelectorAll('[data-look-variant]:not([disabled])')
        );
        if (!group.length) return;
        const at = group.indexOf(chip);
        let next;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = group.length - 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (at + 1) % group.length;
        else next = (at - 1 + group.length) % group.length;
        group[next].focus();
      }

      toggleCard(card) {
        if (!card) return;
        const sel = this.selectionFor(card);

        if (sel.checked) {
          sel.checked = false;
          sel.variantId = '';
          // Untick means "not this one", not "this one, none of it". Ticking it
          // again starts at one rather than at a count already forgotten.
          sel.quantity = 1;
        } else {
          sel.checked = true;
          // Single-variant products commit on the first tap. Multi-variant ones
          // stay tentative even when only one variant is in stock: auto-picking
          // hides the chooser the shopper expects and locks them into an option
          // they never chose.
          sel.variantId = card.dataset.variantId || '';
        }

        this.renderCard(card);
        this.afterSelectionChange();
      }

      pickVariant(card, variantId) {
        if (!card) return;
        const sel = this.selectionFor(card);
        sel.checked = true;
        sel.variantId = sel.variantId === variantId ? '' : variantId;
        this.renderCard(card);
        this.afterSelectionChange();
      }

      /* ── quantity ──────────────────────────────────────────────────── */

      stepQuantity(card, delta) {
        if (!card) return;
        const sel = this.selectionFor(card);
        // Only a committed card shows a stepper. Anything else reaching here is
        // a stray event, not an instruction.
        if (!sel.checked || !sel.variantId) return;
        const next = this.clampQuantity(this.clampQuantity(sel.quantity) + delta);
        if (next === sel.quantity) return;
        sel.quantity = next;
        this.renderQuantity(card);
        this.afterSelectionChange();
      }

      /** The field is typed into as well as stepped, so both events are heard:
       *  `input` follows the keystrokes and `change` — blur, Enter, the native
       *  arrows — is where whatever is left in it is settled. */
      onQtyInput(event) {
        const field = event.target.closest?.('[data-look-qty-input]');
        if (!field) return;
        const card = field.closest('[data-look-card]');
        if (!card) return;
        const sel = this.selectionFor(card);
        if (!sel.checked || !sel.variantId) return;

        const raw = field.value.trim();
        const typed = Math.floor(Number(raw));
        const legible = raw !== '' && Number.isFinite(typed) && typed >= 1;

        // Mid-edit an empty field is a number half-typed and a 0 is a 1 being
        // reached for, so neither is recorded and neither is rewritten under
        // the caret; `change` is where a field below the floor is corrected.
        // The ceiling is enforced on both, because no further digit brings a
        // number already above it back into range.
        if (event.type !== 'change' && !legible) return;

        const next = this.clampQuantity(legible ? typed : 1);
        const moved = next !== sel.quantity;
        sel.quantity = next;
        if (field.value !== String(next)) field.value = String(next);
        this.renderQuantity(card);
        if (moved) this.afterSelectionChange();
      }

      /** Blur commits the field through the `change` path above, which clamps
       *  both the number on screen and the one in state. */
      settleQuantityField() {
        const field = document.activeElement;
        if (field && field.matches?.('[data-look-qty-input]') && this.contains(field)) field.blur();
      }

      afterSelectionChange() {
        this.syncMainButton();
        this.persistSelections();
      }

      /* ── rendering ─────────────────────────────────────────────────── */

      renderCard(card) {
        const sel = this.selectionFor(card);
        const committed = sel.checked && !!sel.variantId;
        const tentative = sel.checked && !sel.variantId;

        card.classList.toggle('complementary-look-card--committed', committed);
        card.classList.toggle('complementary-look-card--tentative', tentative);

        const toggle = card.querySelector('[data-look-toggle]');
        // aria-pressed says "this is in your batch". A tentative card is not —
        // the main button is locked because of it.
        toggle?.setAttribute('aria-pressed', String(committed));

        const expand = card.querySelector('[data-look-expand]');
        if (expand) {
          toggle?.setAttribute('aria-expanded', String(sel.checked));
          expand.classList.toggle('complementary-look-card__expand-wrap--open', sel.checked);
          expand.setAttribute('aria-hidden', String(!sel.checked));
          const chips = Array.from(expand.querySelectorAll('[data-look-variant]'));
          const picked = chips.find((c) => c.dataset.variantId === sel.variantId);
          // Roving tabindex: the group is one tab stop. The picked chip owns it,
          // otherwise the first selectable one does.
          const owner = picked || chips.find((c) => !c.disabled);
          chips.forEach((chip) => {
            const active = chip.dataset.variantId === sel.variantId && committed;
            chip.classList.toggle('complementary-look-card__variant--active', active);
            chip.setAttribute('aria-checked', String(active));
            chip.tabIndex = sel.checked && chip === owner ? 0 : -1;
          });
        }

        const label = card.querySelector('[data-look-variant-label]');
        if (label) {
          const optionName = card.dataset.optionName || 'Seçenek';
          label.textContent = tentative ? `${optionName} Seçiniz` : optionName;
          label.classList.toggle('complementary-look-card__variant-label--prompt', tentative);
        }

        this.renderQuantity(card);

        const pickedOut = card.querySelector('[data-look-picked]');
        if (pickedOut) {
          const chip = committed
            ? card.querySelector(`[data-look-variant][data-variant-id="${sel.variantId}"]`)
            : null;
          pickedOut.hidden = !chip;
          if (chip) {
            card.querySelector('[data-look-picked-value]').textContent = chip.textContent.trim();
          }
        }
      }

      renderQuantity(card) {
        const wrap = card.querySelector('[data-look-qty-wrap]');
        if (!wrap) return;
        const sel = this.selectionFor(card);
        const committed = sel.checked && !!sel.variantId;
        const qty = this.clampQuantity(sel.quantity);

        wrap.classList.toggle('complementary-look-card__qty-wrap--open', committed);
        wrap.setAttribute('aria-hidden', String(!committed));

        const field = wrap.querySelector('[data-look-qty-input]');
        if (field) {
          // Never written back while it is being typed into: assigning .value
          // under the caret jumps it to the end of the field.
          if (document.activeElement !== field && field.value !== String(qty)) {
            field.value = String(qty);
          }
          field.tabIndex = committed ? 0 : -1;
        }

        wrap.querySelectorAll('[data-look-qty]').forEach((button) => {
          const down = button.dataset.lookQty === 'down';
          const spent = down ? qty <= 1 : qty >= LOOK_MAX_QUANTITY;
          // aria-disabled, not the disabled attribute: disabling the element
          // that is currently focused blurs it, and focus lands on <body> — so
          // stepping down to 1 would cost the shopper their place on the page.
          // stepQuantity already refuses a step that changes nothing.
          button.setAttribute('aria-disabled', String(!committed || spent));
          button.tabIndex = committed ? 0 : -1;
        });
      }

      /* ── main button label + lock ──────────────────────────────────── */

      /** Quantity of the page's own product, which the batch also carries. */
      mainQuantity() {
        const input = this.mainForm?.querySelector('[name="quantity"]');
        const n = Number(input?.value || 1);
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      syncMainButton() {
        const button = this.mainButton;
        if (!button) return;
        // Sold out: the label is "Tükendi" and the button is hard-disabled.
        // Appending "(3 ürün)" to it would have the page contradict itself.
        if (button.disabled) {
          this.setStatus('');
          return;
        }
        const text = button.querySelector('.btn__text');
        if (!text) return;

        if (!button.dataset.lookOriginalLabel) {
          button.dataset.lookOriginalLabel = text.textContent.trim();
        }
        const original = button.dataset.lookOriginalLabel;

        const pending = this.pendingCard();
        const lines = this.committedLines();
        const count = lines.length;
        // Units, not cards: two of one product and one of another is three
        // things arriving, and the button's "(N ürün)" has to be the number
        // the cart will show.
        const units = lines.reduce((sum, line) => sum + line.quantity, 0);

        if (pending) {
          const optionName = pending.dataset.optionName || 'Seçenek';
          text.textContent = `${optionName} Seçiniz`;
          // aria-disabled, not the disabled attribute: that one reflects stock,
          // and ProductForm already refuses to submit when aria-disabled is set.
          button.setAttribute('aria-disabled', 'true');
          this.setStatus(`${optionName} seçilmeden sepete eklenemez`);
          return;
        }

        button.removeAttribute('aria-disabled');
        const total = units + this.mainQuantity();
        text.textContent = units > 0 ? `${original} (${total} ürün)` : original;
        // The two numbers only differ once something is ticked more than once,
        // and when they do the line has to say both — "3 ürün seçildi" for two
        // products reads as a miscount of the ticks the shopper can see.
        const chosen = count === units ? `${count} ürün` : `${count} ürün · ${units} adet`;
        this.setStatus(units > 0 ? `${chosen} seçildi · Sepete Ekle ile birlikte eklenecek` : '');
      }

      /** Empty message means "back to the instruction" — the line always says
       *  something, so it never collapses and shifts the cards. */
      setStatus(message) {
        if (!this.status) return;
        const next = message || this.status.dataset.lookIdle || '';
        if (this.status.textContent.trim() !== next) this.status.textContent = next;
        this.status.classList.toggle('complementary-look__intro--count', !!message);
      }

      restoreMainButton() {
        const button = this.mainButton;
        const text = button?.querySelector('.btn__text');
        if (!text || !button.dataset.lookOriginalLabel) return;
        text.textContent = button.dataset.lookOriginalLabel;
        button.removeAttribute('aria-disabled');
      }

      /* ── errors ────────────────────────────────────────────────────── */

      showError(message) {
        const box = this.errorBox;
        if (!box) {
          this.setStatus(message);
          return;
        }
        box.textContent = message;
        box.removeAttribute('hidden');
        box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      clearError() {
        const box = this.errorBox;
        if (box) box.setAttribute('hidden', '');
      }

      /* ── batched add to cart ───────────────────────────────────────── */

      onSubmitCapture(event) {
        const form = this.mainForm;
        if (!form || event.target !== form) return;

        // A field still under the caret has not fired `change` yet, so what it
        // shows can be ahead of what the batch knows. Pressing the buy button
        // does not blur it first — the click lands on a button, and the field's
        // own commit would come after this handler had already read the lines.
        this.settleQuantityField();

        // The theme's own double-submit guard lives behind the listener this
        // handler pre-empts, so it has to be repeated here.
        if (this.submitting || this.mainButton?.getAttribute('aria-disabled') === 'true') {
          const pending = this.pendingCard();
          if (!pending && !this.submitting) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (pending) this.focusPending(pending);
          return;
        }

        const pending = this.pendingCard();
        if (pending) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.focusPending(pending);
          return;
        }

        const extras = this.committedLines();
        if (!extras.length) return; // nothing ticked — the theme's own path, untouched

        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitBatch(form, extras, event.submitter);
      }

      focusPending(card) {
        card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        card.querySelector('[data-look-variant]:not([disabled])')?.focus();
      }

      submitBatch(form, extras, submitter) {
        const button = this.mainButton;
        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput || variantInput.disabled) return;

        this.submitting = true;
        this.clearError();

        const mainLine = {
          id: Number(variantInput.value),
          quantity: this.mainQuantity(),
        };

        // Line item properties and subscription plans belong to the main product
        // and would be silently dropped by a naive rebuild of the payload.
        const properties = {};
        form.querySelectorAll('[name^="properties["]').forEach((input) => {
          if (input.disabled) return;
          if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
          const key = input.name.slice('properties['.length, -1);
          if (key) properties[key] = input.value;
        });
        if (Object.keys(properties).length) mainLine.properties = properties;

        const sellingPlan = form.querySelector('[name="selling_plan"]');
        if (sellingPlan && sellingPlan.value) mainLine.selling_plan = Number(sellingPlan.value);

        // Underscore-prefixed properties are hidden from the customer but reach
        // the order, which is the only way to tell later how much of the basket
        // this row actually earned. Cost: a line carrying a property is its own
        // cart line, so the same variant added here and from its own page shows
        // twice. Deliberate — drop this key to undo it.
        const attributed = extras.map((line) => ({
          ...line,
          properties: { _birlikte_al: this.dataset.productHandle || '1' },
        }));

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          items: [mainLine, ...attributed],
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        button?.classList.add('btn--loading');
        button?.setAttribute('aria-disabled', 'true');

        fetch(FoxTheme.routes.cart_add_url, { ...FoxTheme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              // A multi-line /cart/add is all-or-nothing: when one ticked product
              // has just sold out, NOTHING is added, the main product included.
              // Saying so is the whole point — the ticks stay so the shopper can
              // untick the offender and try again.
              this.showError(parsedState.description || parsedState.message || '');
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } })
              );
              return;
            }

            const cartJson = await (
              await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })
            ).json();
            cartJson.sections = parsedState.sections;

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(
              new CustomEvent('product-ajax:added', { detail: { product: parsedState } })
            );

            // Everything ticked is now in the cart; leaving the ticks on would
            // invite a second identical batch on the next press.
            this.clearSelections();
            this.markInCart(cartJson);

            document.querySelector('cart-drawer')?.show(submitter || button);
          })
          .catch((error) => {
            console.error(error);
            this.showError('Sepete eklenemedi. Bağlantınızı kontrol edip tekrar deneyin.');
          })
          .finally(() => {
            this.submitting = false;
            button?.classList.remove('btn--loading');
            button?.removeAttribute('aria-disabled');
          });
      }

      /** One of six slots spent on something the shopper already owns is a slot
       *  wasted; the cart is in hand after every add, so say so. */
      markInCart(cartJson) {
        const inCart = new Set((cartJson?.items || []).map((i) => String(i.variant_id)));
        this.cards.forEach((card) => {
          const ids = [card.dataset.variantId].concat(
            Array.from(card.querySelectorAll('[data-look-variant]')).map((c) => c.dataset.variantId)
          );
          card.classList.toggle('complementary-look-card--in-cart', ids.some((id) => id && inCart.has(id)));
        });
      }

      clearSelections() {
        this.selections.clear();
        this.cards.forEach((card) => this.renderCard(card));
        this.syncMainButton();
        this.persistSelections();
      }

      /* ── carousel ──────────────────────────────────────────────────── */

      /** The gap is a CSS decision; reading it back keeps one number in one
       *  place. (This theme has already been bitten once by the same default
       *  living in two files and silently disagreeing.) */
      cardStep() {
        const card = this.cards[0];
        if (!card || !this.scroller) return 0;
        const gap = parseFloat(getComputedStyle(this.scroller).columnGap);
        return card.getBoundingClientRect().width + (Number.isFinite(gap) ? gap : 12);
      }

      scrollByPage(direction) {
        if (!this.scroller) return;
        const step = this.cardStep();
        if (!step) return;
        // A page, not a card: the arrow should uncover what is off-screen, and
        // one card per click makes six products a three-click errand.
        const perPage = Math.max(1, Math.floor(this.scroller.clientWidth / step));
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scroller.scrollBy({
          left: step * perPage * direction,
          behavior: reduce ? 'auto' : 'smooth',
        });
      }

      /**
       * Disabled, never hidden. Removing a focused button from the document
       * drops focus to <body>, and the next Tab restarts from the top of the
       * page — which is exactly what happened on the last click of the row.
       */
      onScroll() {
        if (!this.scroller) return;
        const prev = this.querySelector('[data-look-scroll="prev"]');
        const next = this.querySelector('[data-look-scroll="next"]');
        const max = this.scroller.scrollWidth - this.scroller.clientWidth;
        const atStart = this.scroller.scrollLeft <= 1;
        const atEnd = this.scroller.scrollLeft >= max - 1;
        if (prev) {
          prev.disabled = atStart;
          prev.setAttribute('aria-disabled', String(atStart));
        }
        if (next) {
          next.disabled = atEnd;
          next.setAttribute('aria-disabled', String(atEnd));
        }
        // A row that does not overflow has nothing to scroll to.
        this.classList.toggle('complementary-look--static', max <= 1);
      }
    }
  );
}
